import {
  DashboardOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  ToolOutlined,
  DeleteOutlined,
  IdcardOutlined,
  UsergroupAddOutlined,
  HistoryOutlined,
  SafetyCertificateOutlined,
  PrinterOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { Menu } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AdminUserMenu from './AdminUserMenu';
import { stripPathLanguage } from '../../i18nLanguages';
import MyButtonText from '../../components/myButton/MyButtonText';
import usePermission from '../../hooks/usePermission';
export const getSelectedKey = (pathname) => {
  if (pathname.startsWith('/dashboard')) return '/dashboard';
  if (pathname.startsWith('/definitions') || pathname.startsWith('/constants'))
    return '/definitions';
  if (pathname.startsWith('/report')) return '/reports';
  if (pathname.startsWith('/job_repairs/deleted'))
    return '/job_repairs/deleted';
  if (pathname.startsWith('/job_repairs')) return '/job_repairs';
  if (pathname.startsWith('/job_repair_settings'))
    return '/job_repair_settings';
  if (pathname.startsWith('/users')) return '/users';
  if (pathname.startsWith('/activity_log')) return '/activity_log';
  if (pathname.startsWith('/permissions')) return '/permissions';
  if (pathname.startsWith('/clients')) return '/clients';
  if (pathname.startsWith('/profile')) return '/profile';
  return pathname;
};

const USERS_CHILD_KEYS = ['/users', '/activity_log', '/permissions'];

const getOpenKeys = (selectedKey) => {
  if (USERS_CHILD_KEYS.includes(selectedKey)) return ['users_permissions'];
  return [];
};

// Drops menu entries the current policy does not grant. A group disappears when
// its own permission fails or when every one of its children was filtered out.
const filterMenuItems = (items, can) =>
  items
    .map((item) => {
      if (item.permission && !can(item.permission)) return null;
      if (!item.children) return item;

      const children = filterMenuItems(item.children, can);
      return children.length ? { ...item, children } : null;
    })
    .filter(Boolean);

// The menu the current user may see. Shared with the mobile bottom bar.
export const useAdminMenuItems = () => {
  const { t } = useTranslation();
  const { can } = usePermission();
  const isPlatformAdmin = useSelector(
    (state) => state.currentUser?.isPlatformAdmin
  );

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: t('WORD_DASHBOARD'),
      permission: 'useDashboard',
    },
    {
      key: '/job_repairs',
      icon: <ToolOutlined />,
      label: t('JR_JOB_REPAIRS'),
      permission: 'viewJobRepairs',
    },
    {
      key: '/job_repairs/deleted',
      icon: <DeleteOutlined />,
      label: t('JR_DELETED_JOB_REPAIRS'),
      permission: 'viewDeletedJobRepairs',
    },
    {
      key: '/reports',
      icon: <FileTextOutlined />,
      label: t('WORD_REPORTS'),
      permission: 'useReports',
    },
    {
      key: '/definitions',
      icon: <AppstoreOutlined />,
      label: t('WORD_DEFINITIONS'),
      permission: 'useDefinitions',
    },
    {
      key: '/job_repair_settings',
      icon: <PrinterOutlined />,
      label: t('JR_SETTINGS'),
      permission: 'useJobRepairSettings',
    },
    {
      key: 'users_permissions',
      icon: <IdcardOutlined />,
      label: t('WORD_USERS_AND_PERMISSIONS'),
      permission: 'useUsersPermissions',
      children: [
        {
          key: '/users',
          icon: <UsergroupAddOutlined />,
          label: t('WORD_USERS'),
          permission: 'viewUsers',
        },
        {
          key: '/activity_log',
          icon: <HistoryOutlined />,
          label: t('WORD_USER_TRACKING'),
          permission: 'useActivityLog',
        },
        {
          key: '/permissions',
          icon: <SafetyCertificateOutlined />,
          label: t('WORD_PERMISSIONS'),
          permission: 'viewPermissions',
        },
      ],
    },
    isPlatformAdmin && {
      key: '/clients',
      icon: <ShopOutlined />,
      label: t('WORD_CLIENTS'),
    },
  ].filter(Boolean);

  return filterMenuItems(menuItems, can);
};

const AdminSider = ({ onSelect, collapsed, onCollapse }) => {
  const { pathname } = useLocation();
  const selectedKey = getSelectedKey(stripPathLanguage(pathname));
  const { t } = useTranslation();
  const menuItems = useAdminMenuItems();

  return (
    <div className="admin_sider_inner">
      <div className="admin_brand">
        {!collapsed && (
          <>
            <div className="admin_brand_mark">R</div>
            <div className="admin_brand_text">
              <strong>{t('WORD_APP_NAME')}</strong>
              <span>{t('WORD_ADMIN')}</span>
            </div>
          </>
        )}
        <MyButtonText
          className="admin_collapse_btn"
          onClick={onCollapse}
          aria-label={t(collapsed ? 'WORD_EXPAND_SIDEBAR' : 'WORD_COLLAPSE_SIDEBAR')}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </MyButtonText>
      </div>

      <Menu
        mode="inline"
        items={menuItems}
        selectedKeys={[selectedKey]}
        defaultOpenKeys={getOpenKeys(selectedKey)}
        onClick={({ key }) => {
          if (key.startsWith('/')) onSelect?.(key);
        }}
        className="admin_menu"
      />

      <div className="admin_sider_footer">
        <AdminUserMenu />
      </div>
    </div>
  );
};

export default AdminSider;
